import { useCallback } from 'react'

import { useChartStore } from '@/stores/chartStore'
import { useLayoutStore } from '@/stores/layoutStore'
import type { GridSize } from '@/types/chart'
import { GRID_LAYOUTS } from '@/types/chart'
import { cn } from '@/utils/cn'

const GRID_SIZES: GridSize[] = [1, 2, 4, 6, 8, 12, 16]

// Visual grid preview — mirrors the actual panel layout as tiny filled cells.
function LayoutPreview({ size, active }: { size: GridSize; active: boolean }) {
  const { cols, rows } = GRID_LAYOUTS[size]
  const total = cols * rows

  return (
    <span
      aria-hidden
      className="inline-grid"
      style={{
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, 1fr)`,
        width: cols === 1 ? 12 : cols === 2 ? 14 : 16,
        height: rows === 1 ? 10 : rows === 2 ? 12 : rows === 3 ? 13 : 14,
        gap: '1.5px',
      }}
    >
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={cn('rounded-[1px]', active ? 'bg-tv-blue' : 'bg-tv-text-secondary')}
        />
      ))}
    </span>
  )
}

interface LayoutSelectorProps {
  /** If true, renders as a horizontal row (for toolbar). Default: true. */
  horizontal?: boolean
}

export function LayoutSelector({ horizontal = true }: LayoutSelectorProps) {
  const { gridSize, setGridSize } = useLayoutStore()
  const { initPanels } = useChartStore()

  const handleSelect = useCallback(
    (size: GridSize) => {
      setGridSize(size)
      initPanels(size)
    },
    [setGridSize, initPanels]
  )

  return (
    <div
      role="toolbar"
      aria-label="Chart layout"
      className={cn('flex items-center', horizontal ? 'flex-row gap-0.5' : 'flex-col gap-1')}
    >
      {GRID_SIZES.map((size) => {
        const isActive = gridSize === size
        const label = `${size} panel${size > 1 ? 's' : ''}`
        return (
          <button
            key={size}
            type="button"
            onClick={() => handleSelect(size)}
            title={label}
            aria-label={label}
            aria-pressed={isActive}
            className={cn(
              'flex h-7 items-center justify-center rounded px-1.5 transition-colors',
              isActive
                ? 'bg-tv-bg-tertiary'
                : 'text-tv-text-secondary hover:bg-tv-bg-tertiary hover:text-tv-text-primary'
            )}
          >
            <LayoutPreview size={size} active={isActive} />
          </button>
        )
      })}
    </div>
  )
}
